import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAICopilotStore, useCopilotSync } from "../../ai/copilot";
import { AICopilotDrawer } from "../ai/AICopilotDrawer";
import { AiDbCommandBar } from "../ai/AiDbCommandBar";
import { AiInspectorPanel } from "../ai/AiInspectorPanel";
import { IntegrationSyncHeader } from "../integrations/IntegrationSyncHeader";
import { Sidebar } from "./Sidebar";
import { TrialWelcomeGate } from "./TrialWelcomeGate";

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isPosFocus = pathname === "/pos" || pathname.startsWith("/pos/");
  const [mobileOpen, setMobileOpen] = useState(false);
  const isCopilotOpen = useAICopilotStore((state) => state.isOpen);
  const toggleCopilot = useAICopilotStore((state) => state.toggleCopilot);
  useCopilotSync({ route: pathname });

  return (
    <div className="godo-app-bg flex h-screen flex-col overflow-hidden">
      {mobileOpen && !isPosFocus && (
        <button
          type="button"
          aria-label={t("labels.close_menu")}
          className="fixed inset-0 z-40 bg-slate-900/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {!isPosFocus && (
          <aside
            className={`print:hidden h-full w-72 shrink-0 overflow-hidden transform transition-transform duration-200 ease-out md:relative md:translate-x-0 ltr:left-0 rtl:right-0 ${
              mobileOpen
                ? "fixed inset-y-0 z-50 translate-x-0"
                : "max-md:fixed max-md:inset-y-0 max-md:z-50 max-md:hidden max-md:ltr:-translate-x-full max-md:rtl:translate-x-full"
            }`}
          >
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        )}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <header className="z-30 shrink-0 border-b border-slate-200 bg-white/95 backdrop-blur print:hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-3">
                {!isPosFocus && (
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 p-2 text-slate-600 md:hidden"
                  onClick={() => setMobileOpen((open) => !open)}
                  aria-expanded={mobileOpen}
                  aria-label={t("labels.open_menu")}
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden
                  >
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                )}
                <div>
                  <h1 className="text-base font-semibold text-godo-primary">
                    {isPosFocus ? t("nav.pos") : t("labels.app_name")}
                  </h1>
                  {!isPosFocus && (
                    <p className="text-xs text-slate-500">{t("labels.session_active")}</p>
                  )}
                </div>
                {isPosFocus && (
                  <Link
                    to="/dashboard"
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {t("errors.back_to_dashboard")}
                  </Link>
                )}
              </div>
              {!isPosFocus && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <IntegrationSyncHeader />
                  <AiDbCommandBar />
                  <AiInspectorPanel />
                  <button
                    type="button"
                    className={[
                      "rounded-lg border px-3 py-1.5 text-xs font-medium transition",
                      isCopilotOpen
                        ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                    ].join(" ")}
                    onClick={() => toggleCopilot()}
                  >
                    {t("nav.group.intelligence")}
                  </button>
                </div>
              )}
            </div>
          </header>

          <main
            className={
              isPosFocus
                ? "min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-1"
                : "min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 lg:px-3"
            }
          >
            <div key={pathname} className="min-h-0">
              {children}
            </div>
          </main>
        </div>
      </div>
      {!isPosFocus && <AICopilotDrawer />}
      <TrialWelcomeGate skipOnPos={isPosFocus} />
    </div>
  );
}
